import { Link, useNavigate, useLocation } from "react-router-dom";
import { Linkedin, Twitter, Facebook, Instagram } from "lucide-react";
import type { BasicField, Configuration, Field, FooterConfig, Page, socialMediaConfig } from "@/types/config";
import { normalizeDefault } from "@/utils/validationUtils";
import React, { useEffect, useState } from "react";
// import Intercom from "@intercom/messenger-js-sdk";
import { useAuth } from "@/utils/authContext";
import logger from '@/utils/logger';
interface FooterProps {
  configData: any[];
  yourLogo: string;
  base_path?: string;
}

declare global {
  interface Window {
    footerConfig?: {
      email?: string;
      mobileNumber?: string;
      website?: string;
      address?: string;
    };
  }
}

export default function Footer({ configData, yourLogo, base_path }: FooterProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = useAuth();
  const [showFooter, setShowFooter] = useState<FooterConfig | null>(null);
  const [SocialMedia, setSocialMedia] = useState<socialMediaConfig | null>(null);
  const [address, setAddress] = useState<string>("");
  const [footerDescription, setFooterDescription] = useState<string>("");
  const [website, setWebsite] = useState<string>("");
  const [facebook, setFacebook] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [mobileNumber, setMobileNumber] = useState<string>("");
  const [twitter, setTwitter] = useState<string>("");
  const [insta, setInsta] = useState<string>("");
  const [linkedIn, setLinkedIn] = useState<string>("");
  const [terms, setTerms] = useState<string>("");
  const [privacy, setPrivacy] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<string>("");
  const [modalTitle, setModalTitle] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);


  // // Initialize Intercom
  // useEffect(() => {
  //   // Initialize Intercom with your app ID
  //   Intercom({
  //     app_id: 'uzi0kcnq',
  //   });

  //   return () => {
  //     // Clean up Intercom if needed when component unmounts
  //     // This is optional as Intercom doesn't provide a specific cleanup method
  //   };
  // }, []);


  const isPrivacyPolicyEnabled = (config: Page[]): boolean => {
    // Check if Privacy Policy page is enabled in the configuration
    const privacyPolicyPageConfig = config.find((page) => page.name.toLowerCase().includes("privacy policy"));
    const privacyPolicyEnabled = privacyPolicyPageConfig?.configurations.find(
      (field) => field.key === "do_you_need_privacy_policy_page"
    )?.default;

    // Now we only check if the Privacy Policy page is enabled
    return privacyPolicyEnabled === "yes" || privacyPolicyEnabled === true;
  };

  useEffect(() => {
    // Ensure configData is an array before processing
    if (!Array.isArray(configData)) {
      logger.warn("configData is not an array:", configData);
      return;
    }

    const landingPageConfig = configData.find((page) => page.name.toLowerCase().includes("landing page"));

    if (landingPageConfig) {
      const fields: Field[] = landingPageConfig.configurations.map((cfg: Configuration) => {
        if (cfg.fields) {
          return {
            key: cfg.key,
            label: cfg.label,
            fields: cfg.fields.map((field: any) => ({
              key: field.key,
              type: field.type,
              label: field.label,
              default: normalizeDefault(field.default),
              disabled: field.disabled,
            })),
          };
        } else {
          return {
            key: cfg.key,
            type: cfg.type || "unknown",
            label: cfg.label,
            default: normalizeDefault(cfg.default),
            disabled: cfg.disabled,
          };
        }
      });

      const footerConfig = fields.find((item) => item.key === "footer") as FooterConfig;
      setShowFooter(footerConfig);

      const socialmediaConfig = fields.find((item) => item.key === "social media") as socialMediaConfig;
      setSocialMedia(socialmediaConfig);



    }
  }, [configData]);


  useEffect(() => {
    // Fetch contentJson for dynamic data
    const fetchContent = async () => {
      try {
        setIsLoading(true);
        const url = `${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error("Failed to fetch contentJson");
        const data = await response.json();
        const landing = data.find((page: any) => page.name?.toLowerCase().includes("landing page"));
        const values = data.find((page: any) => page.name?.toLowerCase().includes("terms & conditions"));
        if (values?.fields?.[0]?.components?.[0]?.components) {
          const termLen = values.fields[0].components[0].components.length;
          for (let i = 0; i < termLen; i++) {
            const component = values.fields[0].components[0].components[i];
            if ("key" in component) {
              if (component.key === "terms_conditionsDocument" && component.defaultValue?.length > 0) {
                setTerms(component.defaultValue[0].data.text);
                break;
              } else if (component.key === "TermsText") {
                setTerms(component.defaultValue);
              }
            }
          }
        }

        const privacyvalues = data.find((page: any) => page.name === "Privacy Policy");
        if (privacyvalues?.fields?.[0]?.components?.[0]?.components) {
          const privacyLen = privacyvalues.fields[0].components[0].components.length;
          for (let i = 0; i < privacyLen; i++) {
            const component = privacyvalues.fields[0].components[0].components[i];
            if ("key" in component) {
              if (component.key === "privacyPolicyDocument" && component.defaultValue?.length > 0) {
                setPrivacy(component.defaultValue[0].data.text);
                break;
              } else if (component.key === "privacyPolicyTemplate") {
                setPrivacy(component.defaultValue);
              }
            }
          }
        }
        if (landing?.fields) {
          // Find the Footers panel
          let footersPanel;
          for (const field of landing.fields) {
            if (Array.isArray(field.components)) {
              footersPanel = field.components.find((comp: any) => comp.type === "panel" && comp.title?.includes("Footer"));
              if (footersPanel) break;
            }
          }
          let FooterField = footersPanel?.components;
          if (Array.isArray(FooterField)) {
            const addressField = FooterField.find((field: any) => field.key === "address");
            const websiteField = FooterField.find((field: any) => field.key === "WebsiteUrl");
            const emailField = FooterField.find((field: any) => field.key === "email");
            const mobileField = FooterField.find((field: any) => field.key === "MobileNumber");
            const footerDescriptionField = FooterField.find((field: any) => field.key === "footerDescription");
            const emailValue = emailField?.defaultValue || "";
            const mobileValue = mobileField?.defaultValue || "";
            const addressValue = addressField?.defaultValue || "";
            const websiteValue = websiteField?.defaultValue || "";
            setAddress(addressField?.defaultValue || "");
            setWebsite(websiteField?.defaultValue || "");
            setEmail(emailField?.defaultValue || "");
            setMobileNumber(mobileField?.defaultValue || "");
            setFooterDescription(footerDescriptionField?.defaultValue || "");
            if (typeof window !== 'undefined') {
              window.footerConfig = {
                email: emailValue,
                mobileNumber: mobileValue,
                website: websiteValue,
                address: addressValue
              };
            }
          } else {
            setAddress("");
            setWebsite("");
            setEmail("");
            setMobileNumber("");
            setFooterDescription("");
          }
          const socialMediaLinkField = landing.fields.find((field: any) => field.key === "SocialmediaLinks");
          if (socialMediaLinkField?.components?.[0]?.components) {
            const smComponents = socialMediaLinkField.components[0].components;
            setFacebook(smComponents.find((c: any) => c.key === "facebookLink")?.defaultValue || "");
            setTwitter(smComponents.find((c: any) => c.key === "twitterLink")?.defaultValue || "");
            setInsta(smComponents.find((c: any) => c.key === "instagramLink")?.defaultValue || "");
            setLinkedIn(smComponents.find((c: any) => c.key === "linkedInLink")?.defaultValue || "");
          }

          // setAddress(addressField?.defaultValue || "");
          // setWebsite(websiteField?.defaultValue || "");
          // setEmail(emailField?.defaultValue || "");
          // setMobileNumber(mobileField?.defaultValue || "");
          // setFooterDescription(footerDescriptionField?.defaultValue || "");
        }
      } catch (e) {
        // fallback to static
      } finally {
        setIsLoading(false);
      }
    };
    fetchContent();
  }, []);

  const isFieldEnabledfooterfunction = (key: string, config: FooterConfig | null) => {
    if (!config || !config.fields) {
      return true;
    }
    const fieldConfig = config.fields.find((field) => field.key === key);
    return fieldConfig ? normalizeDefault((fieldConfig as BasicField).default) : true;
  };

  // Updated: Look for 'social media links' config group for individual icon toggles
  const isFieldEnabledLink = (key: string, config: Page[]) => {
    // Find the 'Landing Page' config
    const landingPage = config.find((page) => page.name.toLowerCase().includes("landing page"));
    if (!landingPage || !Array.isArray(landingPage.configurations)) return true;
    // Find the 'social media links' group
    const socialLinksConfig = landingPage.configurations.find((cfg: any) => cfg.key === "social media links");
    if (!socialLinksConfig || !Array.isArray(socialLinksConfig.fields)) return true;
    const fieldConfig = socialLinksConfig.fields.find((field: any) => field.key === key);
    return fieldConfig ? normalizeDefault(fieldConfig.default) : true;
  };

  const isFieldEnabledFooter = (key: string) => isFieldEnabledfooterfunction(key, showFooter);

  // Modal Handlers
  const handleShowModal = (content: string, title: string) => {
    setModalContent(content || "Content not available");
    setModalTitle(title);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalContent("");
    setModalTitle("");
  };



  return (
    <footer className="bg-gray-900 text-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            {yourLogo ? (
              <img
                src={yourLogo}
                alt="Logo"
                className="h-12 w-auto mb-4"
                style={{ maxWidth: 160 }}
              />
            ) : (
              <h4 className="text-2xl font-bold text-emerald-400 mb-4">MW Market</h4>
            )}
            {isFieldEnabledFooter("footer description") && (
              <p className="text-gray-300 mb-6 max-w-md">
                {footerDescription}
              </p>
            )}
            {isFieldEnabledFooter("social media") && (
              <div className="flex space-x-4">
                {isFieldEnabledLink("linkedin", configData) && linkedIn && (
                  <a href={linkedIn} className="text-primary-600 hover:text-primary-500 transition-colors" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                    <Linkedin className="h-6 w-6" />
                  </a>
                )}
                {isFieldEnabledLink("facebook", configData) && facebook && (
                  <a href={facebook} className="text-primary-600 hover:text-primary-500 transition-colors" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                    <Facebook className="h-6 w-6" />
                  </a>
                )}
                {isFieldEnabledLink("instagram", configData) && insta && (
                  <a href={insta} className="text-primary-600 hover:text-primary-500 transition-colors" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                    <Instagram className="h-6 w-6" />
                  </a>
                )}
                {isFieldEnabledLink("twitter", configData) && twitter && (
                  <a href={twitter} className="text-primary-600 hover:text-primary-500 transition-colors" aria-label="Twitter X" target="_blank" rel="noopener noreferrer">
                    {/* Replace old Twitter icon with X icon */}
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
                      <path d="M17.53 3H21L14.19 10.39L22.09 21H15.66L10.87 14.62L5.56 21H2L9.13 13.03L1.61 3H8.18L12.5 8.73L17.53 3ZM16.41 19H18.23L7.7 5H5.77L16.41 19Z" />
                    </svg>
                  </a>
                )}
              </div>
            )}
          </div>

          <div>
            <h6 className="font-semibold mb-2">Quick Links</h6>
            <ul className="space-y-2">
              {(() => {
                const landingPage = configData?.find((page: any) => page.name.toLowerCase().includes("landing page"));
                let showHome = true;
                if (landingPage && Array.isArray(landingPage.configurations)) {
                  const landingConfig = landingPage.configurations.find((cfg: any) => cfg.key === "do_you_need_landing_page");
                  if (landingConfig && landingConfig.default === false) showHome = false;
                }
                return !showHome ? (
                  <li>
                    <button
                      onClick={() => navigate(`${base_path}/`)}
                      className="text-gray-300 hover:text-emerald-400 transition-all duration-300 cursor-pointer flex items-center group bg-transparent border-none p-0"
                    >
                      Home
                    </button>
                  </li>
                ) : null;
              })()}
              {/* Hide Explore if config disables it */}
              {(() => {
                const landingPage = configData?.find((page: any) => page.name === "Landing Page");
                let showExplore = true;
                if (landingPage && Array.isArray(landingPage.configurations)) {
                  const exploreConfig = landingPage.configurations.find((cfg: any) => cfg.key === "Explore");
                  if (exploreConfig && exploreConfig.default === false) showExplore = false;
                }
                return showExplore ? (
                  <li>
                    <button
                      onClick={() => {
                        if (isLoggedIn) {
                          // Logged in: go to propertylist or scroll to top if already there
                          if (location.pathname.includes('/propertylist')) {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          } else {
                            localStorage.setItem('clearStartCampaignFilters', 'true');
                            navigate(`${base_path}/propertylist`);
                          }
                        } else {
                          // Not logged in: go to explore or scroll to top if already there
                          if (location.pathname.includes('/explore')) {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          } else {
                            navigate(`${base_path}/explore`);
                          }
                        }
                      }}
                      className="text-gray-300 hover:text-emerald-400 transition-all duration-300 cursor-pointer flex items-center group bg-transparent border-none p-0"
                    >
                      Explore
                    </button>
                  </li>
                ) : null;
              })()}
              <li>
                <button
                  onClick={() => {
                    if (location.pathname.includes('/mycart')) {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      navigate(`${base_path}/mycart`);
                    }
                  }}
                  className="text-gray-300 hover:text-emerald-400 transition-all duration-300 cursor-pointer flex items-center group bg-transparent border-none p-0"
                >
                  Cart
                </button>
              </li>
              {(() => {
                const landingPage = configData?.find((page: any) => page.name.toLowerCase().includes("landing page"));
                let showCampaigns = true;
                if (landingPage && Array.isArray(landingPage.configurations)) {
                  const loginConfig = landingPage.configurations.find((cfg: any) => cfg.key === "login");
                  if (loginConfig && loginConfig.default === false) showCampaigns = false;
                }
                return showCampaigns ? (
                  <li>
                    <button
                      onClick={() => {
                        if (isLoggedIn) {
                          if (location.pathname.includes('/my-campaigns')) {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          } else {
                            navigate(`${base_path}/my-campaigns`);
                          }
                        } else {
                          navigate(`${base_path}/login`);
                        }
                      }}
                      className="text-gray-300 hover:text-emerald-400 transition-all duration-300 cursor-pointer flex items-center group bg-transparent border-none p-0"
                    >
                      Campaigns
                    </button>
                  </li>
                ) : null;
              })()}

            </ul>
          </div>

          {(() => {
            const anyContactEnabled = [
              isFieldEnabledFooter("contact email"),
              isFieldEnabledFooter("address"),
              isFieldEnabledFooter("website"),
              isFieldEnabledFooter("mobile")
            ].some(Boolean);
            if (!anyContactEnabled) return null;
            return (
              <div>
                <h6 className="font-semibold mb-2">Contact Us</h6>
                <ul className="space-y-2">
                  {isFieldEnabledFooter("contact email") && email && (
                    <li className="flex items-center space-x-2 text-gray-300">
                      <svg className="h-5 w-5 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16v16H4z" /><path d="M22 6l-10 7L2 6" /></svg>
                      <a
                        href={`mailto:${email}`}
                        className="hover:text-emerald-400 transition-colors text-gray-300"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {email}
                      </a>
                    </li>
                  )}
                  {isFieldEnabledFooter("website") && website && (
                    <li className="flex items-center space-x-2 text-gray-300">
                      <svg className="h-5 w-5 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 0 20M12 2a15.3 15.3 0 0 0 0 20" /></svg>
                      <a
                        href={website.startsWith('http') ? website : `https://${website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-emerald-400 transition-colors text-gray-300"
                      >
                        {website}
                      </a>
                    </li>
                  )}
                  {/* {isFieldEnabledFooter("mobile") && mobileNumber && (
                    <li className="flex items-center space-x-2 text-gray-300">
                      <svg className="h-5 w-5 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92V19a2 2 0 0 1-2.18 2A19.86 19.86 0 0 1 3 5.18 2 2 0 0 1 5 3h2.09a2 2 0 0 1 2 1.72c.13 1.13.37 2.24.72 3.32a2 2 0 0 1-.45 2.11l-1.27 1.27a16 16 0 0 0 6.29 6.29l1.27-1.27a2 2 0 0 1 2.11-.45c1.08.35 2.19.59 3.32.72A2 2 0 0 1 21 16.91z" /></svg>
                      <a
                        href={`tel:${mobileNumber.replace(/\s/g, '')}`}
                        className="hover:text-emerald-400 transition-colors text-gray-300"
                      >
                        {mobileNumber}
                      </a>
                    </li>
                  )} */}
                  {isFieldEnabledFooter("address") && address && (
                    <li className="flex items-start space-x-2 text-gray-300">
                      <span className="flex-shrink-0 flex items-center">
                        <svg className="h-5 w-5 text-emerald-400 min-w-[20px] min-h-[20px]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10.35V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V10.35a2 2 0 0 1 .76-1.53l7-5.11a2 2 0 0 1 2.48 0l7 5.11a2 2 0 0 1 .76 1.53z" /></svg>
                      </span>
                      <span className="break-words">{address}</span>
                    </li>
                  )}
                </ul>
              </div>
            );
          })()}


        </div>

        <hr className="my-4 border-gray-700" />

        <div className="flex flex-col md:flex-row md:justify-between items-center text-gray-400 mt-0 mb-0 pr-16">
          <div className="mb-2 md:mb-0 text-left w-full md:w-auto">
            © {new Date().getFullYear()} Moving Walls Market. All rights reserved.
          </div>
          <div className="flex space-x-4 text-right w-full md:w-auto justify-center md:justify-end">
            {isFieldEnabledFooter("terms&conditions") && terms && (
              <button
                type="button"
                className="text-gray-400 hover:text-emerald-400 transition-colors focus:outline-none"
                onClick={() => handleShowModal(terms, "Terms & Conditions")}
              >
                Terms & Conditions
              </button>
            )}
            {isFieldEnabledFooter("terms&conditions") && isPrivacyPolicyEnabled(configData) && isFieldEnabledFooter("privacy policy") && (
              <span className="text-gray-600">|</span>
            )}
            {isPrivacyPolicyEnabled(configData) && isFieldEnabledFooter("privacy policy") && (
              <button
                type="button"
                className="text-gray-400 hover:text-emerald-400 transition-colors focus:outline-none"
                onClick={() => handleShowModal(privacy, "Privacy Policy")}
              >
                Privacy Policy
              </button>
            )}
          </div>
        </div>
        {/* Modal for Terms and Privacy */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4">
              <div className="flex justify-between items-center border-b px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">{modalTitle}</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto text-gray-800" dangerouslySetInnerHTML={{ __html: modalContent }} />
              <div className="flex justify-end px-6 py-4 border-t">
                <button
                  onClick={handleCloseModal}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </footer>
  );
}
